import '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import LogoutIcon from '@mui/icons-material/Logout';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import authActions from 'store/app/auth/action';
import getDeviceFriendlyName from 'utils/getDeviceFriendlyName';
import getDeviceIcon from 'utils/getDeviceIcon';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';

const listItemSx = theme => ({
  mb: 1,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiListItemText-root': {
    minHeight: 48,
    borderBottom: 0,
  },
});

const listItemContainerSx = theme => ({
  '& .MuiListItemText-root': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '&:last-child .MuiListItemText-root': {
    borderBottom: 0,
  },
});

const DevicesForm = ({ company, employee }) => {
  const auth = useActions(authActions);
  const token = useSelector(state => state.auth.token);
  const isCurrentDevice = useCallback(device => device.hash === token.hash, [token.hash]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const locale = useSelector(state => state.i18n.locale);

  // Fetch existing devices
  const loadDevices = useCallback(async () => {
    try {
      setLoading(true);
      const {
        value: { data },
      } = await auth.listSessions(employee._id);
      setDevices(data.tokens);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setErrorMessage('Failed to load device information');
    } finally {
      setLoading(false);
    }
  }, [auth, employee._id]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Open delete confirmation dialog
  const openDeleteDialog = device => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
  };

  // Confirm and delete a device
  const confirmDeleteDevice = async () => {
    if (!company?._id || !deviceToDelete) return;

    try {
      setDeleting(true);
      if (isCurrentDevice(deviceToDelete)) {
        await auth.logout();
      } else {
        await auth.deleteSession(employee._id, deviceToDelete._id);
      }
      // Update the local state
      setDevices(devices.filter(device => device._id !== deviceToDelete._id));
      closeDeleteDialog();
    } catch (error) {
      console.error('Error deleting device:', error);
      setErrorMessage('Failed to delete device');
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  // Render device content based on loading status
  const renderDeviceContent = () => {
    // Loading state
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // No devices state
    if (devices.length === 0) {
      return (
        <Box
          sx={theme => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.palette.background.paper,
            border: `1px dashed ${theme.palette.divider}`,
          })}
        >
          <Box sx={{ mb: 2 }}>
            <DevicesIcon sx={{ color: 'primary.main', fontSize: 48 }} />
          </Box>
          <Typography variant="body1" gutterBottom>
            {I18n.t('SettingsDrawer.No_Devices', 'No authenticated devices')}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {I18n.t(
              'SettingsDrawer.Devices_Description',
              'This shows the devices that have been used to access the account',
            )}
          </Typography>
        </Box>
      );
    }

    // Devices list
    return (
      <>
        <Typography variant="body2" color="textSecondary" paragraph>
          {I18n.t(
            'SettingsDrawer.Devices_Info',
            'These are the devices that have been used to access this account. You can remove access for any device.',
          )}
        </Typography>

        <Box
          sx={theme => ({
            overflowY: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
          })}
        >
          <List disablePadding>
            {devices.map(device => {
              const createdAt = device.createdAt
                ? moment(device.createdAt).locale(locale).format('LLL')
                : '';
              const lastActiveDate = device.updatedAt
                ? moment(device.updatedAt).locale(locale).fromNow()
                : '';
              const deviceFriendlyName = getDeviceFriendlyName(
                device.os?.name,
                device.device?.type === 'desktop' ? device.userAgent : device.device.model,
              );

              return (
                <ListItem key={device.id} sx={[listItemContainerSx, listItemSx]}>
                  <Box sx={{ mr: 2 }}>
                    {getDeviceIcon(device.os?.name, device.userAgent, deviceFriendlyName)}
                  </Box>
                  <ListItemText
                    primary={
                      <>
                        {deviceFriendlyName}
                        {device.fireBaseToken && (
                          <Tooltip title={I18n.t('SettingsDrawer.Can_authorize')}>
                            <MobileFriendlyIcon
                              sx={{ fontSize: 16, verticalAlign: 'sub', ml: 0.5 }}
                            />
                          </Tooltip>
                        )}
                        {isCurrentDevice(device) && (
                          <Typography
                            component="span"
                            sx={{ display: 'inline', fontWeight: 'bold', color: 'success.main' }}
                            inline
                          >
                            {' '}
                            ({I18n.t('SettingsDrawer.Current_Device')})
                          </Typography>
                        )}
                      </>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: 14, color: 'text.secondary' }}
                          title={
                            device.updatedAt
                              ? moment(device.updatedAt).locale(locale).format('LLL')
                              : ''
                          }
                        >
                          {I18n.t('SettingsDrawer.Last_Activity', { fromNow: lastActiveDate })}
                        </Typography>
                        {createdAt && (
                          <Typography
                            variant="body2"
                            sx={{ fontSize: 14, color: 'text.secondary' }}
                          >
                            {I18n.t('SettingsDrawer.Created_On', { date: createdAt })}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={I18n.t('SettingsDrawer.Logout_Device', 'Log out of device')}>
                      <IconButton edge="end" onClick={() => openDeleteDialog(device)} size="large">
                        <LogoutIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </>
    );
  };

  return (
    <Box
      component="form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 455,
        padding: '20px 45px 45px 40px',
        height: '100vh',
        overflowY: 'hidden',
      }}
    >
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 'bold',
          fontStyle: 'normal',
          fontStretch: 'normal',
          lineHeight: '38px',
        }}
      >
        {I18n.t('SettingsDrawer.Device_Management', 'Device Management')}
      </Typography>
      <Box
        sx={{ marginTop: '40px', display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}
      >
        {errorMessage && (
          <Typography color="error" variant="body2" gutterBottom>
            {errorMessage}
          </Typography>
        )}
        {renderDeviceContent()}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {I18n.t('SettingsDrawer.Logout_Device_Title', 'Logout Device')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {I18n.t(
              'SettingsDrawer.Logout_Device_Confirmation',
              'Are you sure you want to logout from this device? The user will be signed out on this device and will need to sign in again.',
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            {I18n.t('Cancel', 'Cancel')}
          </Button>
          <Button onClick={confirmDeleteDevice} color="error" autoFocus disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : I18n.t('Logout', 'Logout')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DevicesForm.propTypes = {
  company: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  employee: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
};
export default DevicesForm;
